import { Fragment } from 'react'
import { Link } from 'react-router'
import ArrowForward from '@mui/icons-material/ArrowForward'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import OpenInNew from '@mui/icons-material/OpenInNew'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  alpha,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { InvoiceFieldAvailability } from '@/hooks/useDocumentInvoiceData'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import { getMaxDecimals, sumDecimalStrings } from '@/utils/decimal'
import { getDocumentViewUrl } from '@/utils/document-overlay'
import {
  RelatedInvoice,
  relatedInvoiceAnchorId,
  relatedLineAnchorId,
} from '@/utils/related-invoices'
import { FieldItem, formatValue } from './shared'

/** What a tie's PO side shows — looked up from the POs already loaded in the view. */
export type TiedPOLineItem = {
  poNumber: string
  description: string | null
}

type RelatedInvoiceCardProps = {
  invoice: RelatedInvoice
  /** PO line item id → its PO number and description. */
  poLineItems: Map<string, TiedPOLineItem>
  /** PO id → PO number, for the tie summary. */
  poNumbers: Map<string, string>
  /** Hides values the project has no field for, matching the focused invoice's card. */
  availability: InvoiceFieldAvailability
  /** Held by the PO view, since it decides where this card's connectors start. */
  expanded: boolean
  onToggleExpanded: () => void
  /** Registers the summary row and each tie row as points the card's connectors can start from. */
  anchorRefCallback?: (anchorId: string) => (el: HTMLElement | null) => void
  onHoverChange?: (hovering: boolean) => void
}

function formatAmount(value: string | null) {
  return formatValue(value, {
    decimals: value ? Math.max(2, getMaxDecimals([value])) : 2,
    prefix: '$',
  })
}

/**
 * A compact card for another invoice that's matched to the same PO line items
 * or POs as the focused invoice. Shows its workflow state, vendor, PO number
 * and total, and expands to list all its lines, showing which PO line each
 * tied line goes to.
 */
export default function RelatedInvoiceCard({
  invoice,
  poLineItems,
  poNumbers,
  availability,
  expanded,
  onToggleExpanded,
  anchorRefCallback,
  onHoverChange,
}: RelatedInvoiceCardProps) {
  const getThemeColor = useGetThemeColor()
  const stateColor = invoice.workflowState
    ? getThemeColor(invoice.workflowState.color)
    : undefined

  const tiedPoNumbers = [
    ...new Set(invoice.ties.map((tie) => poNumbers.get(tie.poId) ?? tie.poId)),
  ]
  // One line can be matched to more than one PO line, so count lines, not ties.
  const lineCount = new Set(invoice.ties.map((tie) => tie.invoiceRowId)).size
  // How much of the invoice is tied to these POs, which can be well under its
  // total. Each tied line counts once, even when it's tied to more than one
  // PO line.
  const linkedAmounts = invoice.lines
    .filter((line) => line.ties.length > 0)
    .map((line) => line.amount)
  const linkedTotal = linkedAmounts.some(Boolean)
    ? sumDecimalStrings(linkedAmounts)
    : null

  return (
    <Card
      elevation={2}
      data-testid="related-invoice-card"
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      sx={{ width: '85%', borderRadius: 2, bgcolor: 'background.paper' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ bgcolor: 'action.hover', pl: 2, pr: 0.5, py: 0.5, minHeight: 40 }}
      >
        <ReceiptLongIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Tooltip title={invoice.name} enterDelay={500} disableInteractive>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
          >
            {invoice.name}
          </Typography>
        </Tooltip>
        {invoice.workflowState && stateColor && (
          <Chip
            size="small"
            label={invoice.workflowState.name}
            sx={{
              maxWidth: 160,
              fontWeight: 600,
              color: stateColor,
              bgcolor: alpha(stateColor, 0.16),
            }}
          />
        )}
        <Tooltip title="Open in PO view in a new tab" disableInteractive>
          <IconButton
            size="small"
            aria-label={`Open ${invoice.name} in a new tab`}
            component={Link}
            to={getDocumentViewUrl({
              documentId: invoice.id,
              projectId: invoice.projectId,
              workflowState: invoice.workflowState,
              view: 'purchase-order',
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Stack
          direction="row"
          spacing={3}
          useFlexGap
          flexWrap="wrap"
          alignItems="flex-end"
        >
          {availability.vendor && (
            <FieldItem label="Vendor" value={invoice.vendorName} />
          )}
          {availability.poNumber && (
            <FieldItem label="PO#" value={invoice.poNumber} mono />
          )}
          {(availability.invoiceTotal || availability.amount) && (
            <Box
              data-testid="related-invoice-total"
              sx={{ ml: 'auto', textAlign: 'right' }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.2, mb: 0.25 }}
              >
                {[
                  availability.amount && 'Linked',
                  availability.invoiceTotal && 'Total',
                ]
                  .filter(Boolean)
                  .join(' / ')}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ fontFamily: 'monospace' }}
              >
                {availability.amount && formatAmount(linkedTotal)}
                {availability.amount && availability.invoiceTotal && (
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary', fontWeight: 400 }}
                  >
                    {' / '}
                    {formatAmount(invoice.invoiceTotal)}
                  </Box>
                )}
                {!availability.amount && formatAmount(invoice.invoiceTotal)}
              </Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ mt: 1.5, mb: 0.5 }} />

        <Button
          variant="text"
          size="small"
          color="inherit"
          fullWidth
          ref={anchorRefCallback?.(relatedInvoiceAnchorId(invoice.id))}
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          sx={{
            justifyContent: 'space-between',
            textTransform: 'none',
            color: 'text.secondary',
            px: 0.5,
          }}
        >
          <Typography variant="caption" noWrap>
            {lineCount} {lineCount === 1 ? 'line' : 'lines'} tied to PO
            {tiedPoNumbers.length === 1 ? '' : 's'}{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {tiedPoNumbers.join(', ')}
            </Box>
          </Typography>
        </Button>

        <Collapse in={expanded} unmountOnExit>
          <Stack spacing={1} sx={{ pt: 0.5, pb: 1 }}>
            {invoice.lines.map((line) => {
              const isTied = line.ties.length > 0
              return (
                <Stack
                  key={line.rowId}
                  ref={anchorRefCallback?.(relatedLineAnchorId(line.rowId))}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  data-testid="related-invoice-line"
                  data-tied={isTied ? 'true' : undefined}
                  // Untied lines are context, not connections, so they recede.
                  sx={{ opacity: isTied ? 1 : 0.5 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    {availability.description && (
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                        {line.description || '—'}
                      </Typography>
                    )}
                    {availability.amount && (
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace', flexShrink: 0 }}
                      >
                        {formatAmount(line.amount)}
                      </Typography>
                    )}
                  </Stack>
                  <ArrowForward
                    sx={{
                      fontSize: 14,
                      color: 'text.disabled',
                      flexShrink: 0,
                      visibility: isTied ? 'visible' : 'hidden',
                    }}
                  />
                  <Typography
                    variant="caption"
                    color={isTied ? 'text.secondary' : 'text.disabled'}
                    noWrap
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    {isTied
                      ? line.ties.map((tie, index) => {
                          const poLineItem = poLineItems.get(tie.poLineItemId)
                          return (
                            <Fragment key={tie.matchId}>
                              {index > 0 && '; '}
                              <Box
                                component="span"
                                sx={{ fontFamily: 'monospace' }}
                              >
                                PO{' '}
                                {poLineItem?.poNumber ??
                                  poNumbers.get(tie.poId)}
                              </Box>
                              {poLineItem?.description
                                ? ` · ${poLineItem.description}`
                                : ''}
                            </Fragment>
                          )
                        })
                      : 'Not matched'}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        </Collapse>
      </Box>
    </Card>
  )
}
